import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, OnInit, signal, untracked, viewChild, ViewEncapsulation, afterNextRender } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { applySequentialPercentageDiscounts } from '@phoenix/common';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { IShowConfirmationDialogInput, simpleDeepClone } from '@phx-erp/shared';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { Dialog, DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { SkeletonModule } from 'primeng/skeleton';
import { SplitterModule } from 'primeng/splitter';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { CalculateListHeightPipe } from '../../../../@core/pipes/calculate-list-height.pipe';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { Address, DeletionResult, Operator, PriceListEntry, PriceListType, Product, SearchInput, VendorPriceListAddressLink, VendorPriceListAddressLinkInput } from '../../../../generated/generated-types';
import { AddressDropDownComponent } from '../../../crm/modules/company-drop-down/company-drop-down/address-drop-down.component';
import { PriceListEntryService } from '../../services/price-list-entry.service';
import { VendorPriceListAddressLinkService } from '../../services/vendor-price-list-address-link.service';
import { PriceListDisplayRowWidgetComponent } from '../price-list-display-row-widget/price-list-display-row-widget.component';
import { PriceListEntryListWidgetComponent } from '../price-list-entry-list-widget/price-list-entry-list-widget.component';
import { AddVendorPriceListDetailWidgetComponent } from '../vendor-price-list-detail-widget/vendor-price-list-detail-widget.component';
import { VendorProductEditWidgetComponent } from '../vendor-product-edit-widget/vendor-product-edit-widget.component';
import { HandlePriceListoptions } from './type-interfaces';

@Component({
  selector: 'app-vendor-product-price-edit-widget',
  templateUrl: './vendor-product-price-edit-widget.component.html',
  styleUrls: ['./vendor-product-price-edit-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslatePipe,
    ButtonModule,
    ListboxModule,
    TooltipModule,
    PriceListEntryListWidgetComponent,
    AddressDropDownComponent,
    BadgeModule,
    SkeletonModule,
    FormsModule,
    CalculateListHeightPipe,
    PriceListDisplayRowWidgetComponent,
    NgxUiLoaderModule,
    PhxCheckboxComponent,
    VendorProductEditWidgetComponent,
    DialogModule,
    SplitterModule,
  ]
})
export class VendorProductPriceEditWidgetComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  private baseServices = inject(BaseListCombinedService);
  private vendorPriceListAddressLinkService = inject(VendorPriceListAddressLinkService);
  private priceListEntryService = inject(PriceListEntryService);
  private currentPriceEntryRequestId = 0;

  product = model.required<Product>();

  dataTestid = input.required<string>();

  vendorPriceListAddressLinkSearchInput: SearchInput;

  PriceListType = PriceListType;

  priceListEntryHistoryListWidget = viewChild<PriceListEntryListWidgetComponent>('priceListEntryHistoryListWidget');

  // From Here i havent seen how/where these are used
  priceListEntries: Partial<PriceListEntry>[] = [];
  priceListEntriesOld: PriceListEntry[];
  savedPriceListEntry: PriceListEntry;
  copyAddress: boolean = false; // if true, the customer price list will be copied, if false, it will be edited
  //  To Here i havent seen how/where these are used

  minHeightInPt = signal<number>(200);
  copyOrEditPriceListDialogVisible = signal<boolean>(false) // the dialog to choose the customer to copy to or to edit
  historyCheckedSig = signal<boolean>(false);
  selectedAddressSig = signal<Address>(null);
  fieldsSig = signal<VendorPriceListAddressLink[]>([]);
  priceListLinkNodeSelectedSig = signal<VendorPriceListAddressLink>(null);
  currentPriceEntriesByPriceListIdSig = signal<Record<string, PriceListEntry>>({});

  priceListEntryListWidget = viewChild<PriceListEntryListWidgetComponent>('priceListEntryListWidget');
  addressDropDown = viewChild<AddressDropDownComponent>('addressDropDown');
  chooseDialogSig = viewChild<Dialog>('chooseDialog');

  isLoadingSig = signal<boolean>(true); //we load always right in the beginning
  /** First paint of Einkauf tab without splitter/lists; mount on next frame. */
  deferHeavyUi = signal(false);

  // Purchase price reference product computed signals
  hasPurchasePriceReferenceProduct = computed(() => {
    const product = this.product();
    return !!(product?.purchasePriceReferenceProduct || product?.purchasePriceReferenceProductId);
  });

  purchasePriceReferenceProductIdentifier = computed(() => {
    const product = this.product();
    return (product?.purchasePriceReferenceProduct?.identifier ?? '') + ' - ' + (product?.purchasePriceReferenceProduct?.description ?? '');
  });

  purchasePriceReferenceProductId = computed(() => {
    const product = this.product();
    return product?.purchasePriceReferenceProduct?.id ?? '';
  });

  /**
   * Open the purchase price reference product in a new tab
   */
  openPurchaseRefInNewTab() {
    const productId = this.purchasePriceReferenceProductId();
    if (productId) {
      window.open(`/products/edit/${productId}`, '_blank');
    }
  }

  /**
   * Effective product id to use for loading vendor relations and entries.
   * If purchase prices are inherited, use the referenced product id.
   */
  effectiveVendorsProductId = computed(() => {
    const product = this.product();
    if (this.hasPurchasePriceReferenceProduct()) {
      return product?.purchasePriceReferenceProductId ?? product?.purchasePriceReferenceProduct?.id ?? product?.id;
    }
    return product?.id;
  });

  constructor() {
    afterNextRender(() => {
      this.deferHeavyUi.set(true);
    });
    effect(() => {
      const priceLists = this.fieldsSig();
      const selected = this.priceListLinkNodeSelectedSig();
      if (priceLists.length === 0) {
        if (selected) {
          untracked(() => this.priceListLinkNodeSelectedSig.set(null));
        }
        return;
      }
      // Keep a valid row if the current list was removed. Do not fill an empty
      // selection — Ctrl/Cmd+click is the intentional unselect path.
      if (selected && !priceLists.some(list => list.id === selected.id)) {
        untracked(() => this.priceListLinkNodeSelectedSig.set(priceLists[0]));
      }
    });
    effect(() => {
      const priceListLinks = this.fieldsSig();
      const productId = this.effectiveVendorsProductId();
      untracked(() => {
        void this.loadCurrentPriceEntries(priceListLinks, productId);
      });
    });
  }

  async ngOnInit() {

    const productIdForVendors = this.effectiveVendorsProductId();
    if (productIdForVendors) {
      this.vendorPriceListAddressLinkSearchInput = {
        searchFilter: {
          rules: [
            {
              field: "productId",
              operator: Operator.equal,
              value: productIdForVendors
            }
          ]
        },
      };
    }
    this.isLoadingSig.set(true); //should already be true
    let res = await lastValueFrom(this.vendorPriceListAddressLinkService.getItems(this.vendorPriceListAddressLinkSearchInput).mapSingle(x => x.getVendorPriceListAddressLinks));
    // reset and populate list
    this.fieldsSig.set([]);
    (res.items as VendorPriceListAddressLink[]).forEach((x) => {
      this.fieldsSig.update((field) => {
        field = field || [];
        return [...field, x];
      });
    });
    this.priceListLinkNodeSelectedSig.set(this.fieldsSig()[0] ?? null);
    this.isLoadingSig.set(false);
  }

  async loadItemsOfPriceList() {
    if (!this.priceListLinkNodeSelectedSig())
      return;

    //TODO filtering - only show current & future prices
    let res = await lastValueFrom(this.priceListEntryService.getItems({
      searchFilter: {
        rules: [
          {
            field: "priceListId",
            operator: Operator.equal,
            value: this.priceListLinkNodeSelectedSig()?.vendorPriceList?.id
          }, {

          }
        ]
      },
      onlyActive: true,
      onlyHistory: false,
      productIds: [this.effectiveVendorsProductId()],
    }).mapSingle(x => x.getPriceListEntries));
    let items = res.items as PriceListEntry[];
    this.priceListEntries = simpleDeepClone(items);
  }

  currentPriceEntryFor(priceListLink: VendorPriceListAddressLink): PriceListEntry | null {
    const priceListId = priceListLink?.vendorPriceList?.id ?? priceListLink?.vendorPriceListId;
    return priceListId ? this.currentPriceEntriesByPriceListIdSig()[priceListId] ?? null : null;
  }

  /**
   * Overview price after vendor discount1/discount2 (list price kept for editing).
   */
  currentNetPriceEntryFor(priceListLink: VendorPriceListAddressLink): PriceListEntry | null {
    const entry = this.currentPriceEntryFor(priceListLink);
    if (!entry || entry.price === null || entry.price === undefined) {
      return entry;
    }
    if (priceListLink?.nonDiscountable) {
      return entry;
    }
    const netPrice = applySequentialPercentageDiscounts(
      entry.price,
      priceListLink?.discount1,
      priceListLink?.discount2,
    );
    if (netPrice === entry.price) {
      return entry;
    }
    return { ...entry, price: netPrice };
  }

  /** True when the overview price differs from the list price because of vendor discounts. */
  isDiscountedOverviewPrice(priceListLink: VendorPriceListAddressLink): boolean {
    const listEntry = this.currentPriceEntryFor(priceListLink);
    const netEntry = this.currentNetPriceEntryFor(priceListLink);
    return listEntry?.price != null && netEntry?.price != null && netEntry.price !== listEntry.price;
  }

  onVendorDetailsSaved(saved: VendorPriceListAddressLink): void {
    if (!saved?.id) {
      return;
    }

    // New link objects so the listbox re-renders net prices; keep vendorPriceList/address
    // identity so [priceList] on the entry list does not refresh.
    let updatedLink: VendorPriceListAddressLink | null = null;
    this.fieldsSig.update((fields) =>
      (fields ?? []).map((field) => {
        if (field.id !== saved.id) {
          return field;
        }
        updatedLink = {
          ...field,
          ...saved,
          vendorPriceList: field.vendorPriceList,
          address: field.address,
        };
        return updatedLink;
      }),
    );

    if (updatedLink && this.priceListLinkNodeSelectedSig()?.id === saved.id) {
      this.priceListLinkNodeSelectedSig.set(updatedLink);
    }
  }

  onPriceEntrySaved(): void {
    this.priceListEntryHistoryListWidget()?.refresh();
    void this.loadCurrentPriceEntries(this.fieldsSig(), this.effectiveVendorsProductId());
  }

  private async loadCurrentPriceEntries(priceListLinks: VendorPriceListAddressLink[], productId: string | null): Promise<void> {
    const requestId = ++this.currentPriceEntryRequestId;
    const priceListIds = priceListLinks
      .map(priceListLink => priceListLink?.vendorPriceList?.id ?? priceListLink?.vendorPriceListId)
      .filter((id): id is string => !!id);

    if (!productId || priceListIds.length === 0) {
      this.currentPriceEntriesByPriceListIdSig.set({});
      return;
    }

    try {
      const priceEntriesResult = await lastValueFrom(this.priceListEntryService.getItems({
        productIds: [productId],
        onlyActive: true,
        onlyOfPriceListIds: priceListIds,
        take: 0
      }).mapSingle(x => x.getPriceListEntries));

      if (requestId !== this.currentPriceEntryRequestId) {
        return;
      }

      const today = new Date();
      const priceEntriesByPriceListId = (priceEntriesResult.items as PriceListEntry[]).reduce<Record<string, PriceListEntry>>((result, priceEntry) => {
        const priceListId = priceEntry.priceListId;
        const startDate = priceEntry.startDate ? new Date(priceEntry.startDate) : null;
        const currentEntry = priceListId ? result[priceListId] : null;
        const currentStartDate = currentEntry?.startDate ? new Date(currentEntry.startDate) : null;
        const isStarted = !startDate || startDate <= today;
        const isNewerThanCurrent = !currentStartDate || (startDate && startDate > currentStartDate);

        if (priceListId && isStarted && isNewerThanCurrent && priceEntry.price !== null && priceEntry.price !== undefined) {
          result[priceListId] = priceEntry;
        }
        return result;
      }, {});

      this.currentPriceEntriesByPriceListIdSig.set(priceEntriesByPriceListId);
    } catch (error: any) {
      if (requestId === this.currentPriceEntryRequestId) {
        this.currentPriceEntriesByPriceListIdSig.set({});
      }
      console.error('Failed to load active vendor product prices for price list overview', error);
    }
  }

  addNewEmptyEntry() {
    this.priceListEntries = [{}, ...this.priceListEntries];
  }

  async saveEntry(entry: PriceListEntry) {

    entry.priceListId = this.priceListLinkNodeSelectedSig().vendorPriceList.id;
    entry.productId = this.product()?.id;

    let date = new Date(entry.startDate);
    let now = new Date();

    if (date.getTime() > 0 && date.getDate() < now.getDate()) {
      let confirmationInput: IShowConfirmationDialogInput = {
        title: "Das Datum liegt in der Vergangenheit, sicher?",
        message: "",
        btnNoText: _("common.no"),
        btnYesText: _("common.yes")
      }
      let sub = this.baseServices.modalService.showConfirmationDialog(confirmationInput)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async result => {
          if (result == true) {
            // delete entry.priceListId; 
            this.savedPriceListEntry = await this.priceListEntryService.saveItem(entry);
            this.priceListEntries = this.priceListEntries.filter(x => x.id != entry.id);
            this.priceListEntries = [...this.priceListEntries];
            // this.cdRef.markForCheck();
            this.baseServices.notificationService.success(_("common.saved"));
            this.priceListEntryListWidget()?.refresh();
            sub.unsubscribe();
          } else {
            sub.unsubscribe();
            this.loadItemsOfPriceList();
          }
        });
    }
    else {
      try {
        this.savedPriceListEntry = await this.priceListEntryService.saveItem(entry);
        this.priceListEntries = this.priceListEntries.filter(x => x.id != entry.id);
        this.priceListEntries = [this.savedPriceListEntry, ...this.priceListEntries];
        this.baseServices.notificationService.success(_("common.saved"));
      }
      catch (e) {
        this.loadItemsOfPriceList();
      }
    }
  }

  async onNewPriceListSaved(vendorPriceListAddressLink: VendorPriceListAddressLink) {

    this.fieldsSig.update((field) => {
      field = field || [];
      return [...field, vendorPriceListAddressLink];
    });

    // Select the newly made price list
    this.priceListLinkNodeSelectedSig.set(vendorPriceListAddressLink);

  }

  async deleteVendorPriceListWithConfirm() {
    let msg = this.baseServices.i18nService.translate(_("common.delete-vendor-price-list-confirm"));
    let sub = this.baseServices.modalService.showDeletionDialog(msg)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async result => {
        if (result == true) {
          let deletitionResponse = await this.vendorPriceListAddressLinkService.deleteItem(this.priceListLinkNodeSelectedSig().id);
          if (deletitionResponse.result == DeletionResult.DELETED) {
            this.baseServices.notificationService.success(_("common.deleted"));
            const deletedId = this.priceListLinkNodeSelectedSig()?.id;
            this.fieldsSig.update((field) => {
              field = field || [];
              return field.filter(x => x.id != deletedId);
            });
            this.priceListLinkNodeSelectedSig.set(this.fieldsSig()[0] ?? null);
            sub.unsubscribe();
          }
        } else {
          sub.unsubscribe();
          return
        }
      });
  }

  async handlePriceList(option: HandlePriceListoptions) {
    switch (option) {
      case "save":
        let ref = this.baseServices.modalService.fromComponent(AddVendorPriceListDetailWidgetComponent, {
          title: this.baseServices.i18nService.translate(_('vendor-price-list.vendor-relation')),
          size: "sm",
          verticalAlign: 'top',
          closable: true,
          locals: {
            address: null,
          },
          setLocalsSig: {
            productId: this.product()?.id,
            dataTestId: this.dataTestid() + '-vendor-price-list-detail-widget'
          }
        })
        let sub = ref.subscribe((data: VendorPriceListAddressLink) => {
          if (data) {
            this.onNewPriceListSaved(data);
          }
          sub.unsubscribe();
        });
        break
      case "copy":
        this.chooseDialogSig().header = this.baseServices.i18nService.translate(_('vendor-price-list.choose-vendor'));
        this.copyOrEditPriceListDialogVisible.set(true);
        this.addressDropDown().formControl.setValue({}); //Bitte auswählen!
        this.addressDropDown().props.placeholder = this.baseServices.i18nService.translate(_('common.please-select'));
        this.copyAddress = true;
        break
      case "edit":
        this.chooseDialogSig().header = this.baseServices.i18nService.translate(_('vendor-price-list.edit-vendor'));
        this.selectedAddressSig.set(this.priceListLinkNodeSelectedSig().address);
        this.addressDropDown().formControl.setValue(this.selectedAddressSig());
        this.fieldsSig.update((field) => {
          field = field || [];
          return field.filter(x => x.id != this.selectedAddressSig().id);
        });
        this.copyOrEditPriceListDialogVisible.set(true);
        break
    }
  }

  async copyOrSaveVendorPriceList() {
    if (!this.selectedAddressSig())
      this.baseServices.notificationService.warning(_("vendor-price-list.choose-address-first"));

    if (this.copyAddress) {
    }
    else { // the edit branch of this method
      let vendorPriceListInput: VendorPriceListAddressLinkInput = {
        vendorPriceListId: this.priceListLinkNodeSelectedSig()?.vendorPriceList?.id ?? this.priceListLinkNodeSelectedSig().vendorPriceListId,
        addressId: this.selectedAddressSig().id,
        productId: this.priceListLinkNodeSelectedSig().productId,
        id: this.priceListLinkNodeSelectedSig().id,
      };
      let saved: VendorPriceListAddressLink = await this.vendorPriceListAddressLinkService.saveItem(vendorPriceListInput);

      if (saved) {
        this.fieldsSig.update((field) => {
          field = field || [];
          return field.filter(x => x.id != this.priceListLinkNodeSelectedSig().id);
        });
        this.fieldsSig.update((field) => {
          field = field || [];
          return [...field, saved];
        });
        this.baseServices.notificationService.success(_("common.changed"));
        this.copyOrEditPriceListDialogVisible.set(false);
      }
    }
  }

  getAddressFromFields(): Address[] {
    let address: Address[] = [];
    this.fieldsSig().forEach((field) => {
      if (field?.address)
        address.push(field.address);
    });
    return address;
  }

  addressAlreadyLinked(address: Address): boolean {
    if (address == null) return false;
    return this.getAddressFromFields().find(x => x.id == address.id) ? true : false;
  }

  setSelectedAddress(address: Address | Address[]) {
    if (Array.isArray(address)) {
      //takes last address from array
      this.selectedAddressSig.set(address[address.length - 1]);
    } else {
      this.selectedAddressSig.set(address);
    }
  }
}


